import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import useIsMounted from "../../../hooks/use-is-mounted";
import {DialogActions, DialogContent, DialogTitle, Divider} from "@mui/material";
import Dialog from "../../base/dialog";
import {Col, Row} from "reactstrap";
import Button from "../../base/button/button";
import {useDispatch, useSelector} from "react-redux";
import ReduxStoreRoutes from "../../../../core/models/static/routes/redux-store-routes";
import {setReduxDialogState} from "../../../redux/slices/dialogs/actions";
import CacheService from "../../../../core/services/cache/cache_service";
import Api from "../../../../core/services/api/api";
import DateUtils from "../../../../core/services/date-utils";
import classnames from "classnames";
import LazyImage from "../../app-specific/lazy-image";
import {ReactComponent as ImagePlaceholder} from "../../../../assets/images/operator-profile-placeholder/operator-profile-placeholder-sidebar.svg";
import Form from "../../base/form";
import {MuiRFF, Yup} from "../../form";
import {OutlinedInputForm} from "../../base/input/outlined_input";
import {Add} from "@mui/icons-material";
import {useOperatorInformation} from "../../../hooks/use-operator-information";


const WelcomeToPanelTaskDialog = () => {
    const [loading, setLoading] = useState(false);
    const [isPlanningTodayTasks, setIsPlanningTodayTasks] = useState(false);
    const [doneTasks, setDoneTasks] = useState([]);
    const [leftOutTasks, setLeftOutTasks] = useState([]);
    const [pastDueDateTasks, setPastDueDateTasks] = useState([]);
    const [todayTasks, setTodayTasks] = useState([]);
    const isMounted = useIsMounted();
    const state = useSelector(state => state?.[ReduxStoreRoutes.dialogs.base][ReduxStoreRoutes.dialogs.welcomePanelTaskDialog] ?? {});
    const dispatch = useDispatch();
    const operatorInformation = useOperatorInformation();

    /**
     * Gets list of tasks to be shown in the tasks review section
     * @return {Promise<void>}
     */
    const getTasks = useCallback(async () => {
        setLoading(true);
        const response = await Api.getTodayTaskSummary();
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            setDoneTasks(response?.data?.doneTasks ?? [])
            setLeftOutTasks(response?.data?.leftOutTasks ?? [])
            setPastDueDateTasks(response?.data?.pastDueTasks ?? [])
            setTodayTasks(response?.data?.todaysTasks ?? [])
        }
    }, [isMounted])

    /**
     * With initial render of the component, calls the api for getting the list of current tasks for today
     */
    useEffect(() => {
        getTasks().then()
    }, [getTasks])


    /**
     * Closes the dialog by dispatching new state for the task more info dialog
     * - Calls the callback function for emitting an event to close welcome to panel dialog on other tabs as well
     * - Invokes the optional `onClose` stored in Redux state so the panel dialog
     *   queue can advance to the next eligible entry.
     */
    const closeDialog = (preventFromOpeningAgain) => {
        if (preventFromOpeningAgain)
            CacheService.welcomeToPanelDialogToggled(true, true);
        CacheService.welcomeToPanelDialogToggled(false, true);
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.welcomePanelTaskDialog, {open: false}));
        state.onClose?.();
    }

    /**
     * Adds a new task to the list of today's tasks:
     * - Calls the api regarding this action
     * - if the api call was successful, appends the newly created task to the list
     * @param taskTitle
     * @return {Promise<void>}
     */
    const handleAddingNewTask = useCallback(async (taskTitle) => {
        const forApi = {
            title: taskTitle,
            assignedToOperator: {
                id: operatorInformation?.id,
                firstname: operatorInformation?.firstName ?? '',
                lastname: operatorInformation?.lastName ?? '',
            },
            dueDate: DateUtils.serverNow().endOf('day').format(DateUtils.withoutOffsetFormat),
            description: '',
        }
        const response = await Api.createTask(forApi);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setTodayTasks(prevState => [...prevState, response?.data])
        }
    }, [isMounted, operatorInformation?.firstName, operatorInformation?.id, operatorInformation?.lastName])

    /**
     * Memo version of the content of the dialog
     * @type {JSX.Element}
     */
    const dialogContent = useMemo(() => {
        return isPlanningTodayTasks
            ? <>
                <p className={'page-title'}>Today's Tasks</p>
                {
                    (todayTasks?.length ?? 0) > 0
                        ? todayTasks?.map(task =>
                            <PlanningTaskCard
                                key={task.id}
                                task={task}
                            />
                        )
                        : <div className={'place-holder'}>
                            <p>
                                Currently you have no tasks
                            </p>
                        </div>
                }
                <div className={'add-new-task-container'}>
                    <DefineNewTaskCard onNewTaskAdded={handleAddingNewTask}/>
                </div>
            </>
            : <>
                <p className={'page-title'}>You have done the following tasks</p>
                {
                    (doneTasks?.length ?? 0) > 0
                        ? doneTasks?.map(task =>
                            <PlanningTaskCard
                                key={task.id}
                                task={task}
                                isDone={true}
                            />
                        )
                        : <div className={'place-holder'}>
                            <p>
                                No tasks were done yesterday
                            </p>
                        </div>
                }
                <p className={'page-title'}>These tasks have been left out</p>
                {
                    (leftOutTasks?.length ?? 0) > 0
                        ? leftOutTasks?.map(task =>
                            <PlanningTaskCard
                                key={task.id}
                                task={task}
                            />
                        )
                        : <div className={'place-holder'}>
                            <p>
                                No tasks were left over from yesterday
                            </p>
                        </div>
                }
                <p className={'page-title'}>Past due date</p>
                {
                    (pastDueDateTasks?.length ?? 0) > 0
                        ? pastDueDateTasks?.map(task =>
                            <PlanningTaskCard
                                key={task.id}
                                task={task}
                                isPastDueDate={true}
                            />
                        )
                        : <div className={'place-holder'}>
                            <p>
                                No tasks with passed due date
                            </p>
                        </div>
                }
            </>
    }, [isPlanningTodayTasks, todayTasks, handleAddingNewTask, doneTasks, leftOutTasks, pastDueDateTasks])

    /**
     * If the [isPlanningTodayTasks] flag is set to [False], then changes it to [True] otherwise closes the dialog.
     */
    const onConfirmButtonClicked = () => {
        if (!isPlanningTodayTasks) {
            setIsPlanningTodayTasks(true)
            return;
        }
        closeDialog(true);
    }

    return (
        <>
            <Dialog
                open={state?.open}
                setOpen={() => closeDialog(true)}
                maxWidth={'md'}
                className={'bound-dialog welcome-panel-task-dialog'}
                scroll={'paper'}
                aria-labelledby="scroll-dialog-title"
            >
                <DialogTitle id={'scroll-dialog-title'}>
                    <div className={'dialog-title'}>
                        <p>
                            Welcome to the panel
                        </p>
                        <p>
                            {
                                isPlanningTodayTasks
                                    ? `Let's build a productive day`
                                    : `Let's review yesterday`
                            }
                        </p>
                    </div>
                    <Divider className='divider'/>
                </DialogTitle>
                <DialogContent className={'px-2'}>
                    <div className={'dialog-content'}>
                        {dialogContent}
                    </div>
                </DialogContent>
                <DialogActions className={'p-0'}>
                    <Row className={'w-100 no-gutters'}>
                        <Divider className='divider'/>
                        <Row className='no-gutters justify-content-between align-items-start w-100'>
                            {
                                !isPlanningTodayTasks
                                    ? <Button
                                        disabled={!state?.open || loading}
                                        variant={'dialog'}
                                        color={'primary'}
                                        onClick={() => closeDialog(true)}
                                        borderRadius={'0 8px 0 8px'}
                                    >
                                        Plan later
                                    </Button>
                                    : <div/>
                            }
                            <Button
                                disabled={!state?.open || loading}
                                variant={'dialog'}
                                color={'primary'}
                                type={'button'}
                                borderRadius={'8px 0 8px 0'}
                                onClick={onConfirmButtonClicked}
                            >
                                {
                                    isPlanningTodayTasks
                                        ? "Done"
                                        : "Start planning for today"
                                }
                            </Button>
                        </Row>
                    </Row>
                </DialogActions>
            </Dialog>
        </>
    );
}


/**
 * Component for rendering the tasks in the list
 * @param task
 * @param isDone
 * @param isPastDueDate
 * @return {JSX.Element}
 * @constructor
 */
const PlanningTaskCard = ({
                              task,
                              isDone = false,
                              isPastDueDate = false
                          }) => {
    return (
        <div className={classnames('planning-task-card', {'done': isDone})}>
            <p className={'task-title'}>
                {task?.title}
            </p>
            <p className={'task-description'}>
                {task?.description}
            </p>
            <div className={'task-footer-section'}>
                <div className={'d-flex align-items-center justify-content-start'}>
                    {
                        task?.createdByOperator !== null &&
                        <div className={classnames('assigner-container')}>
                            <div className={'assigner-image-container'}>
                                <LazyImage
                                    alt={'assigner image'}
                                    noImage={<ImagePlaceholder/>}
                                    src={task?.assignerImageUrl ?? ''}
                                    height={27}
                                />
                            </div>
                            <p className={'assigner-text'}>
                                Assigned by <span
                                className={'assigner-name'}>{task?.createdByOperator?.firstname ?? ''} {task?.createdByOperator?.lastname ?? ''}</span>
                            </p>
                        </div>
                    }
                    {
                        !!task?.dueDate &&
                        <div className={classnames('due-date-container', {'passed': isPastDueDate})}>
                            <p>Due Date: <span>{DateUtils.serverToLocal(task?.dueDate).format(DateUtils.dateFormat)} </span></p>
                        </div>
                    }

                </div>
                {
                    !!task?.createdDateTime &&
                    <p className={'created-date'}>{DateUtils.serverToLocal(task?.createdDateTime).format(DateUtils.dateFormat)}</p>
                }
            </div>
        </div>
    )
}


const formNames = {
    title: 'title'
}
const schema = Yup.object().shape({
    [formNames.title]: Yup.string().trim().nullable().required('Title is required'),
});
const validate = MuiRFF.makeValidate(schema);
const required = MuiRFF.makeRequired(schema);
/**
 * Component for creating a new task
 * @param onNewTaskAdded
 * @return {JSX.Element}
 * @constructor
 */
const DefineNewTaskCard = ({onNewTaskAdded}) => {
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    /*** @type {React.MutableRefObject<formApi>}*/
    const formRef = useRef(null);

    /**
     * Toggles adding new task form being visible by updating the [open] values in the inner state
     */
    const toggleOpen = () => {
        setOpen(prevState => !prevState);
    }

    /**
     * Submits the form values for creating a new task:
     * - Calls the function for creating the task with the title
     * - Handles updating the [loading] in the inner state
     * @param values
     * @return {Promise<void>}
     */
    const onSubmit = async (values) => {
        setLoading(true);
        await onNewTaskAdded(values[formNames.title]);
        setTimeout(() => formRef.current.restart({}), 0)
        setLoading(false);
    }

    return (
        <>
            {
                open
                    ? <Form
                        onSubmit={onSubmit}
                        validate={validate}
                        initialValues={{}}
                        render={({submitting, values, form}) => {
                            formRef.current = form;
                            return (
                                <Row className='no-gutters align-items-center justify-content-end mt-4'>
                                    <Col xs={12} className={'px-2 mb-3'}>
                                        <OutlinedInputForm
                                            disabled={loading}
                                            value={values[formNames.title] ?? ''}
                                            label={'Title'}
                                            name={formNames.title}
                                            type={'text'}
                                            required={required[formNames.title]}
                                        />
                                    </Col>
                                    <div className={'d-flex w-100 justify-content-end align-items-center px-2'}>
                                        <Button
                                            disabled={!!loading}
                                            type={'button'}
                                            variant={'text'}
                                            color={'primary'}
                                            onClick={toggleOpen}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            disabled={!!loading}
                                            type={'submit'}
                                            variant={'contained'}
                                            color={'primary'}
                                            className={'px-4'}
                                        >
                                            {
                                                submitting
                                                    ? "Creating..."
                                                    : "Create"
                                            }
                                        </Button>
                                    </div>
                                </Row>
                            );
                        }}
                    />
                    : <Button
                        className={classnames(
                            'd-flex align-items-center justify-content-center',
                        )}
                        variant={'outlined'}
                        color={'primary'}
                        onClick={toggleOpen}
                        disabled={!!loading}
                    >
                        <Add className={'mr-2'}/>
                        <p>
                            Define another task for today
                        </p>
                    </Button>
            }
        </>
    )
}

export default WelcomeToPanelTaskDialog;
